import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';

import {
  cancelPublisherWebsiteImportApi,
  getLatestPublisherWebsiteImportApi,
  launchPublisherWebsiteImportApi,
} from '@api/publishers.api';
import { getCurrentWorkspaceId } from '@redux/selectors/workspaces.selectors';
import queryCacheKeys from '@constants/queryCacheKeys.constants';
import { QueryObjectHookType } from '@ts/common.types';
import { PublisherWebsiteImportType } from '@ts/publishers.types';
import { PublisherWebsiteImportStatus } from 'respona_api/generated/order-publisher_pb';

export const IMPORT_POLL_INTERVAL_MS = 3000;

export type PublisherWebsiteImportHookType = QueryObjectHookType<PublisherWebsiteImportType> & {
  /** True while the latest import of the workspace is still creating websites. */
  isInProgress: boolean;
  isLaunching: boolean;
  isCanceling: boolean;
  launch: (fileUid: string, fileName: string) => Promise<PublisherWebsiteImportType>;
  cancel: () => Promise<PublisherWebsiteImportType>;
};

export const isImportInProgress = (item?: PublisherWebsiteImportType): boolean =>
  item?.id > 0 && item.status === PublisherWebsiteImportStatus.PUBLISHER_WEBSITE_IMPORT_IN_PROGRESS;

/**
 * Latest CSV website import of the current workspace (the backend allows one running import
 * per organization, so "latest" is also "the running one"). Polls every few seconds while the
 * import is in progress and refreshes the websites list when it finishes, so a reload or a
 * closed modal never loses track of a running import.
 *
 * Instantiate it once per page and pass it down: every instance would otherwise report the
 * finish separately.
 */
export default function usePublisherWebsiteImport(
  onFinished?: (item: PublisherWebsiteImportType) => void,
  enabled = true
): PublisherWebsiteImportHookType {
  const queryClient = useQueryClient();
  const workspaceId = useSelector(getCurrentWorkspaceId);
  // "Was the import of THIS workspace in progress at the last observation": scoped to the
  // workspace so switching workspaces mid-import never reports a phantom finish.
  const lastSeenRef = useRef<{ workspaceId: number; inProgress: boolean }>({
    workspaceId,
    inProgress: false,
  });

  const key = [queryCacheKeys.publisherWebsiteImport, workspaceId];

  const refreshWebsites = () => queryClient.invalidateQueries([queryCacheKeys.publisherWebsites]);

  // In react-query v3 `setQueryData` re-runs the query's `onSuccess`, so every write below
  // records the new state in the ref BEFORE writing the cache; otherwise launch/cancel would
  // be mistaken for a finish.
  const rememberState = (item: PublisherWebsiteImportType) => {
    lastSeenRef.current = { workspaceId, inProgress: isImportInProgress(item) };
  };

  const { data, isLoading, isError, error, refetch } = useQuery<PublisherWebsiteImportType>({
    queryKey: key,
    queryFn: () => getLatestPublisherWebsiteImportApi(workspaceId),
    enabled: enabled && !!workspaceId,
    refetchOnWindowFocus: false,
    retry: false,
    refetchInterval: (item) => (isImportInProgress(item) ? IMPORT_POLL_INTERVAL_MS : false),
    onSuccess: (item) => {
      const { workspaceId: seenWorkspaceId, inProgress: wasInProgress } = lastSeenRef.current;
      const finished =
        seenWorkspaceId === workspaceId && wasInProgress && !isImportInProgress(item);

      rememberState(item);

      if (finished) {
        refreshWebsites();
        onFinished?.(item);
      }
    },
  });

  const { mutateAsync: launchMutation, isLoading: isLaunching } = useMutation(
    ({ fileUid, fileName }: { fileUid: string; fileName: string }) =>
      launchPublisherWebsiteImportApi(workspaceId, fileUid, fileName),
    {
      onSuccess: (item) => {
        rememberState(item);
        queryClient.setQueryData<PublisherWebsiteImportType>(key, item);
      },
    }
  );

  const { mutateAsync: cancelMutation, isLoading: isCanceling } = useMutation(
    () => cancelPublisherWebsiteImportApi(data.id, workspaceId),
    {
      onSuccess: (item) => {
        rememberState(item);
        queryClient.setQueryData<PublisherWebsiteImportType>(key, item);
        // Rows that were already being created finish, so the list may have new websites.
        refreshWebsites();
      },
    }
  );

  const updateInCache = (_: number, payload: PublisherWebsiteImportType) =>
    queryClient.setQueryData<PublisherWebsiteImportType>(key, (cachedData) => ({
      ...cachedData,
      ...payload,
    }));

  const evictCacheByKey = () => queryClient.removeQueries(key);

  return {
    item: data,
    refetch,
    isLoading,
    isError,
    error,
    update: updateInCache,
    evictByKey: evictCacheByKey,
    isInProgress: isImportInProgress(data),
    isLaunching,
    isCanceling,
    launch: (fileUid: string, fileName: string) => launchMutation({ fileUid, fileName }),
    cancel: () => cancelMutation(),
  };
}
